import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { notification } from 'antd';

import { BuildingDetails } from '@totem/components/portfolio/buildings/details/types';
import RemoteAccessBuildingAuditContext from '@totem/components/services/remoteAccess/v2/buildingAudit/RemoteAccessBuildingAuditContext';
import {
  QRUserRole,
  StatusFilter,
} from '@totem/components/services/remoteAccess/v2/users/types';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import {
  anyId,
  securityGroupCheckConstraintAny,
} from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { SecurityGroupCheckConstraints } from '@totem/types/user';
import {
  AUTH0_USERS_ROLES_BY_BUILDING_ENDPOINT,
  BUILDINGS_ENDPOINT,
} from '@totem/utilities/endpoints';

type Props = {
  buildingId: string;
  children?: ReactNode;
};

const RemoteAccessBuildingAuditContainer = ({
  buildingId,
  children,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [buildingDetails, setBuildingDetails] =
    useState<BuildingDetails | null>(null);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [allRows, setAllRows] = useState<QRUserRole[]>([]);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [emailSearch, setEmailSearch] = useState<string>('');
  const [nameSearch, setNameSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  useEffect(() => {
    fetch(`${BUILDINGS_ENDPOINT}/${buildingId}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: BuildingDetails) => setBuildingDetails(result ?? null))
      .catch(() => {
        notification.error({ message: 'Failed to load building details.' });
      });
  }, [buildingId, getApiToken]);

  const canView = useMemo(() => {
    let securityCheckConstraints: SecurityGroupCheckConstraints =
      securityGroupCheckConstraintAny;

    if (buildingDetails !== null) {
      securityCheckConstraints = {
        regionId: buildingDetails.region.id,
        buildingId,
        controlSystemId: anyId,
      };
    }

    return permissions.remoteAccess.hasPermissions(
      userProfile,
      securityCheckConstraints,
    );
  }, [userProfile, buildingDetails, buildingId]);

  const canEdit = useMemo(() => {
    let securityCheckConstraints: SecurityGroupCheckConstraints =
      securityGroupCheckConstraintAny;

    if (buildingDetails !== null) {
      securityCheckConstraints = {
        regionId: buildingDetails.region.id,
        buildingId,
        controlSystemId: anyId,
      };
    }

    return permissions.remoteAccessAdmin.hasPermissions(
      userProfile,
      securityCheckConstraints,
    );
  }, [userProfile, buildingDetails, buildingId]);

  useEffect(() => {
    if (!canView) {
      setAllRows([]);
    }
  }, [canView]);

  useEffect(() => {
    if (!refreshData || !canView) {
      return;
    }

    setRefreshData(false);

    setUsersLoading(true);

    fetch(`${AUTH0_USERS_ROLES_BY_BUILDING_ENDPOINT}/${buildingId}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: QRUserRole[]) => {
        setAllRows(result ?? []);
      })
      .catch(() => {
        notification.error({
          message: 'Failed to load remote access audit users.',
        });
      })
      .finally(() => setUsersLoading(false));
  }, [refreshData, canView, buildingId, getApiToken]);

  const onRefresh = useCallback(() => {
    setRefreshData(true);
  }, []);

  const rows = useMemo(() => {
    const emailTerm = emailSearch.trim().toLowerCase();
    const nameTerm = nameSearch.trim().toLowerCase();

    return allRows.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) {
        return false;
      }
      if (emailTerm && !row.user.email?.toLowerCase().includes(emailTerm)) {
        return false;
      }
      if (nameTerm && !row.user.name?.toLowerCase().includes(nameTerm)) {
        return false;
      }
      return true;
    });
  }, [allRows, emailSearch, nameSearch, statusFilter]);

  return (
    <RemoteAccessBuildingAuditContext.Provider
      value={{
        loading: usersLoading,
        buildingId,
        buildingName: buildingDetails?.building.name ?? '',
        canView,
        canEdit,
        rows,
        emailSearch,
        setEmailSearch,
        nameSearch,
        setNameSearch,
        statusFilter,
        setStatusFilter,
        selectedUserIds,
        setSelectedUserIds,
        onRefresh,
      }}
    >
      {children}
    </RemoteAccessBuildingAuditContext.Provider>
  );
};

export default RemoteAccessBuildingAuditContainer;
